import { TextInput, InputStatuses, AlertVariants } from '@carboledger/ui';
import { resolveRowUnitType, MIXED_UNIT_TYPE } from '@/hooks';
import { formatQuantityOrDash, formatPercentOrDash } from '@/lib/format';
import { rowUnitSymbol } from '@/lib/mappers/quantities';
import {
  CellText,
  batchField,
  FULFILL_UNIT_TYPE,
  enteredQuantities,
  remainingAfterEntry,
  addedAfterEntry,
  buildSuggestionGroups,
  getSuggestionGroup,
} from '@/components/common';
import { useGetLinkableDeclarationsForOrderQuery } from '@/lib/api/fulfillApi';
import { buildUnitHeaderOptions } from './declarationUnits';
import { buildAllocateCreditColumns, buildSelectInboundColumns } from './selectInboundColumns';
import {
  buildInboundDiscrepancySections,
  buildDeadlineWarning,
} from '@/components/common/Fulfill/fulfillDiscrepancy';

// Disabled inputs never fire it, but TextInput expects the prop.
const NOOP = () => {};

// Only rows reading in this unit are summed; the link wizards' running totals do the same.
const certifiedTotalInUnit = (selected, values, selections, unitType) =>
  selected.reduce((total, row) => {
    if (resolveRowUnitType(row, selections) !== unitType) return total;
    const amount = Number(batchField(values, row.id, 'certifiedQuantity'));
    return Number.isFinite(amount) ? total + amount : total;
  }, 0);

// Weighted by each row's own certified quantity — an untyped or zero row contributes nothing.
const weightedAverageGhgSavingsInUnit = (selected, values, selections, unitType) => {
  let weightedSum = 0;
  let totalWeight = 0;
  selected.forEach((row) => {
    if (resolveRowUnitType(row, selections) !== unitType) return;
    const quantity = Number(batchField(values, row.id, 'certifiedQuantity'));
    if (!Number.isFinite(quantity) || quantity <= 0 || row.ghgEmissionSavingsRaw == null) return;
    weightedSum += row.ghgEmissionSavingsRaw * quantity;
    totalWeight += quantity;
  });
  return totalWeight > 0 ? weightedSum / totalWeight : null;
};

const buildSelectedColumns = (t, { unitHeaderSelect, onRemove }) =>
  buildAllocateCreditColumns(t, { unitHeaderSelect, onRemove });

// The design puts the totals under their own columns, so they are a real table footer.
const buildFooterRow = (t, { selected, values, selections, unitHeaderSelect, context }) => {
  if (selected.length === 0) return undefined;
  const unitType = unitHeaderSelect.value;
  const unit = rowUnitSymbol(selected[0], unitType);
  const totalCertified = certifiedTotalInUnit(selected, values, selections, unitType);
  const pendingRemaining = context?.remainingByUnit?.[unit]?.toAllocate ?? null;
  const overPending = pendingRemaining != null && totalCertified > pendingRemaining;

  return {
    certifiedQuantity: (
      <TextInput
        value={formatQuantityOrDash(totalCertified, unit)}
        onChange={NOOP}
        disabled
        status={overPending ? InputStatuses.error : undefined}
        message={overPending ? t('ORDERS_FULFILL_TOTAL_EXCEEDS_PENDING') : undefined}
        aria-label={t('ORDERS_FULFILL_TOTAL_CERTIFIED_QUANTITY')}
      />
    ),
    ghgEmissionSavings: (
      <CellText>
        {formatPercentOrDash(
          weightedAverageGhgSavingsInUnit(selected, values, selections, unitType),
        )}
      </CellText>
    ),
  };
};

// The section's own messages, read off the picks themselves — no screen feeds these.
const buildAlerts = (t, { selected, selections }) => [
  {
    id: 'transport',
    variant: AlertVariants.info,
    message: t('ORDERS_FULFILL_TRANSPORTATION_NOTE'),
  },
  ...selected.flatMap((row) =>
    (row.sustainabilityMismatches ?? []).map((mismatch) => ({
      id: `${row.id}-${mismatch.id ?? mismatch.type}`,
      variant: AlertVariants.warning,
      message: mismatch.message,
    })),
  ),
  ...(hasMixedUnits(selected, selections)
    ? [
        {
          id: 'mixed-units',
          variant: AlertVariants.error,
          message: t('ORDERS_FULFILL_MIXED_UNITS_ERROR'),
        },
      ]
    : []),
];

// A `remaining` tile counts down and a `committed` tile counts up by what has been entered.
const tileValue = (tile, { unitValues, unit, remaining, committed, entered }) => {
  if (tile.remaining) {
    return formatQuantityOrDash(
      remainingAfterEntry(remaining?.[tile.remaining], entered[tile.entered]),
      unit,
    );
  }
  if (tile.committed) {
    return formatQuantityOrDash(
      addedAfterEntry(committed?.[tile.committed], entered[tile.entered]),
      unit,
    );
  }
  return unitValues[tile.amount];
};

// The record's headline figures in whichever unit the header reads, live against what is typed.
const buildTiles = (t, { unitHeaderSelect, context, values }) => {
  const unitType = unitHeaderSelect.value;
  const unitValues = context?.record?.byUnit?.[unitType];
  if (!unitValues) return [];
  const unit = unitValues.unit;
  const amounts = {
    unitValues,
    unit,
    remaining: context?.remainingByUnit?.[unit],
    committed: context?.committedByUnit?.[unit],
    entered: enteredQuantities({ unit, unitType, values, assigned: context?.assigned }),
  };

  return (context?.tileFields ?? [])
    .filter((tile) => unitValues[tile.amount] != null)
    .map((tile) => ({ label: t(tile.labelKey, { unit }), value: tileValue(tile, amounts) }));
};

// Fulfil ▸ Select inbound — config for <SelectFromTable>; both fulfil forms use this one.

const buildDiscrepancy = (t, row, unitType) => ({
  referenceId: row.referenceId,
  message: buildDeadlineWarning(t, {
    dateOfDispatch: row.dateOfDispatchRaw,
    pendingQuantity: row.balanceQuantityRaw?.[unitType],
    unit: row.byUnit?.[unitType]?.unit,
  }),
  sections: buildInboundDiscrepancySections(t, row, unitType),
});

// The order this fulfilment is against decides what is eligible; nothing else travels.
const useCandidates = ({ context, search, page, pageSize, enabled }) => {
  const { data, isFetching } = useGetLinkableDeclarationsForOrderQuery(
    { pendingOrderId: context?.pendingOrderId, search, page, limit: pageSize },
    { skip: !context?.pendingOrderId || !enabled },
  );
  return { rows: data?.rows, total: data?.total, loading: isFetching };
};

// A fulfilment allocates on one basis, so two rows in different units cannot be summed.
const hasMixedUnits = (selected, selections) => {
  const units = new Set(selected.map((row) => resolveRowUnitType(row, selections)));
  return selected.length > 0 && (units.size > 1 || units.has(MIXED_UNIT_TYPE));
};

// Which rows are in and on what basis; their typed amounts are already form fields.
const writeSelection = ({ selected, selections, setValue }) => {
  const mixed = hasMixedUnits(selected, selections);

  setValue('selectedInbounds', mixed ? [] : selected.map((row) => row.id));
  setValue(
    'fulfillUnitType',
    selected.length > 0 && !mixed
      ? (FULFILL_UNIT_TYPE[resolveRowUnitType(selected[0], selections)] ?? '')
      : '',
  );
  setValue('hasMixedUnitBatches', mixed);
};

export const SELECT_INBOUND_CONFIG = Object.freeze({
  // Where the section sits
  name: 'selectInbound',
  sectionHeadingKey: 'ORDERS_FULFILL_SECTION_ALLOCATE_CREDIT',
  missingKey: 'ORDERS_FULFILL_NO_INBOUND_SELECTED_ERROR',
  // "Sold to" always comes from the order itself; a missing one gates the whole section.
  isDisabled: (record) => !record.soldTo || record.soldTo === '-',
  disabledNoteKey: 'ORDERS_FULFILL_SOLD_TO_REQUIRED',

  // The button and the modal's chrome
  titleKey: 'ORDERS_FULFILL_SELECT_INBOUND',
  promptKey: 'ORDERS_FULFILL_SELECT_INBOUND_PROMPT',
  emptyKey: 'ORDERS_FULFILL_SELECT_INBOUND_EMPTY',
  selectMode: 'multi',
  // More rows can be added to a selection, so the button never goes away.
  keepPrompt: true,
  maxSelected: 10,
  filterable: true,

  // The modal's own table
  buildColumns: buildSelectInboundColumns,
  unitOptions: buildUnitHeaderOptions,
  buildDiscrepancy,
  // The server's two sections: declarations matching every order preference, then the rest.
  buildGroups: buildSuggestionGroups,
  getRowGroup: getSuggestionGroup,

  // The picked rows
  buildSelectedColumns,
  buildFooterRow,
  buildTiles,
  buildAlerts,

  // The section's own data
  useCandidates,
  writeSelection,
});
